import React, { useMemo, useState } from "react";
import { Pressable, Text, View } from "react-native";

import AIExhibitNarrationPressable from "../audio/AIExhibitNarrationPressable";
import { useAIExhibitFixedNarration } from "../audio/useAIExhibitFixedNarration";
import { AI_EXHIBIT_COLORS as C } from "../aiExhibitTheme";

const NARRATION_BY_TERM_ID = Object.freeze({
  ai: "ai_family_ai",
  ml: "ai_family_machine_learning",
  deep: "ai_family_deep_learning",
  gen: "ai_family_generative_ai",
});
import { getAIExhibitBatch5Copy } from "../content/aiExhibitBatch5Content";

export default function AIFamilyScreen({
  language = "en",
  audioEnabled = false,
}) {
  const { playNarration } = useAIExhibitFixedNarration({
    audioEnabled,
    language,
  });
  const t = getAIExhibitBatch5Copy(language).family;
  const [termId, setTermId] = useState(t.terms[0].id);

  const term = useMemo(
    () => t.terms.find((item) => item.id === termId) || t.terms[0],
    [termId, t.terms]
  );

  return (
    <View style={{ padding: 28 }}>
      <Text style={{ color: C.accent, fontSize: 13, fontWeight: "900", letterSpacing: 1.2 }}>
        {t.eyebrow}
      </Text>

      <Text style={{ color: C.text, fontSize: 44, lineHeight: 50, fontWeight: "900", marginTop: 8 }}>
        {t.title}
      </Text>

      <Text style={{ color: C.muted, fontSize: 18, lineHeight: 27, marginTop: 8, maxWidth: 980 }}>
        {t.subtitle}
      </Text>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12, marginTop: 24 }}>
        {t.terms.map((item) => {
          const active = item.id === termId;

          return (
            <Pressable
              key={item.id}
              onPress={() => {
                setTermId(item.id);
                playNarration(NARRATION_BY_TERM_ID[item.id]);
              }}
              style={({ pressed }) => ({
                flex: 1,
                minWidth: 220,
                minHeight: 130,
                padding: 17,
                borderRadius: 17,
                borderWidth: active ? 2 : 1,
                borderColor: active ? C.accent : C.border,
                backgroundColor: active || pressed ? C.accentSoft : C.surfaceRaised,
              })}
            >
              <Text style={{ fontSize: 30 }}>{item.icon}</Text>
              <Text style={{ color: C.text, fontSize: 19, fontWeight: "900", marginTop: 9 }}>
                {item.title}
              </Text>
              <Text style={{ color: C.muted, fontSize: 13, lineHeight: 19, marginTop: 5 }}>
                {item.short}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <View style={{ marginTop: 18, padding: 22, borderRadius: 18, borderWidth: 1, borderColor: C.accent, backgroundColor: C.surfaceRaised }}>
        <Text style={{ color: C.accent, fontSize: 13, fontWeight: "900" }}>
          {term.title.toUpperCase()}
        </Text>

        <Text style={{ color: C.text, fontSize: 19, lineHeight: 29, marginTop: 9 }}>
          {term.description}
        </Text>

        <View style={{ marginTop: 14, padding: 15, borderRadius: 14, backgroundColor: C.accentSoft }}>
          <Text style={{ color: C.text, fontSize: 16, lineHeight: 24, fontWeight: "800" }}>
            {term.relation}
          </Text>
        </View>

        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
          {term.examples.map((example) => (
            <View
              key={example}
              style={{
                paddingHorizontal: 12,
                paddingVertical: 9,
                borderRadius: 999,
                borderWidth: 1,
                borderColor: C.border,
                backgroundColor: C.surface,
              }}
            >
              <Text style={{ color: C.muted, fontSize: 13, fontWeight: "800" }}>
                {example}
              </Text>
            </View>
          ))}
        </View>
      </View>

      <AIExhibitNarrationPressable
        audioEnabled={audioEnabled}
        onPress={() => playNarration("ai_family_mental_map")}
        style={{ marginTop: 18, padding: 20, borderRadius: 18, borderWidth: 1, borderColor: C.border, backgroundColor: C.surfaceRaised }}
      >
        <Text style={{ color: C.text, fontSize: 20, fontWeight: "900" }}>
          {t.mapTitle}
        </Text>

        <View style={{ gap: 10, marginTop: 14 }}>
          {t.mapLines.map((line, index) => (
            <View key={line} style={{ flexDirection: "row", gap: 10, alignItems: "flex-start" }}>
              <View
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 999,
                  backgroundColor: index === 0 ? C.accentSoft : C.surface,
                  borderWidth: 1,
                  borderColor: C.border,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text style={{ color: C.accent, fontSize: 12, fontWeight: "900" }}>
                  {index + 1}
                </Text>
              </View>
              <Text style={{ color: C.text, fontSize: 16, lineHeight: 24, flex: 1 }}>
                {line}
              </Text>
            </View>
          ))}
        </View>
      </AIExhibitNarrationPressable>

      <View style={{ marginTop: 15, padding: 16, borderRadius: 15, borderLeftWidth: 5, borderWidth: 1, borderColor: C.border, borderLeftColor: C.warning, backgroundColor: C.accentSoft }}>
        <Text style={{ color: C.text, fontSize: 15, lineHeight: 23 }}>
          {t.caution}
        </Text>
      </View>
    </View>
  );
}
